import { useQuery } from '@tanstack/react-query';
import { ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { format } from 'date-fns';
import Avatar from '@/components/Avatar';
import { eventsService } from '@/services/events.service';
import { getGroupColor } from './groupColors';
import type { CalendarEventInstance } from './types';

const CARD_WIDTH = 300;
const CARD_EST_HEIGHT = 170;

interface Props {
  instance: CalendarEventInstance;
  anchorRect: { top: number; bottom: number; left: number; right: number };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Hover preview for a calendar chip. Fetches the RSVP list with the same
 * query key as the slide-over / detail page, so hovering literally prefetches
 * the click.
 */
export default function EventHoverCard({ instance, anchorRect, onMouseEnter, onMouseLeave }: Props) {
  const color = getGroupColor(instance.groupId);

  const rsvpsQuery = useQuery({
    queryKey: [instance.organizationId, 'event-rsvps', instance.id],
    queryFn: () => eventsService.getEventRSVPs(instance.id, instance.organizationId),
  });

  const going = (rsvpsQuery.data?.rsvps ?? []).filter((r) => r.status === 'yes');
  const counts = instance.rsvpCounts;

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const left = Math.min(Math.max(anchorRect.left, 8), viewportW - CARD_WIDTH - 8);
  const flipUp = anchorRect.bottom + CARD_EST_HEIGHT + 12 > viewportH;
  const positionStyle = flipUp
    ? { left, bottom: viewportH - anchorRect.top + 6 }
    : { left, top: anchorRect.bottom + 6 };

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ ...positionStyle, width: CARD_WIDTH }}
      className="fixed z-40 rounded-xl border border-flock-gray-200 bg-white p-3.5 shadow-xl"
      role="tooltip"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold text-flock-navy-900">
          {instance.title}
        </h3>
        <span
          className={clsx(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
            color.chipBg,
            color.chipText
          )}
        >
          {instance.groupName}
        </span>
      </div>

      <div className="mt-1.5 space-y-1 text-xs text-flock-navy-500">
        <p className="flex items-center gap-1.5">
          <ClockIcon className="h-3.5 w-3.5 shrink-0" />
          {instance.allDay
            ? format(instance.occurrenceStart, 'EEE, MMM d')
            : `${format(instance.occurrenceStart, 'EEE, MMM d · h:mm a')}${
                instance.occurrenceEnd ? ` – ${format(instance.occurrenceEnd, 'h:mm a')}` : ''
              }`}
        </p>
        {instance.location && (
          <p className="flex items-center gap-1.5">
            <MapPinIcon className="h-3.5 w-3.5 shrink-0" /> {instance.location}
          </p>
        )}
      </div>

      <div className="mt-2.5 flex items-center border-t border-flock-gray-200 pt-2.5">
        {rsvpsQuery.isLoading ? (
          <span className="h-6 w-32 animate-pulse rounded-full bg-flock-navy-50" />
        ) : (
          <>
            <span className="flex">
              {going.slice(0, 3).map((p, i) => (
                <Avatar
                  key={p.id}
                  name={p.user_name}
                  sizeClass="h-6 w-6"
                  textClass="text-[9px]"
                  className={clsx('ring-2 ring-white', i > 0 && '-ml-1.5')}
                />
              ))}
            </span>
            <span className={clsx('text-xs text-flock-navy-500', going.length > 0 && 'ml-2')}>
              {counts.yes > 0 || counts.maybe > 0
                ? `${counts.yes} going${counts.maybe > 0 ? ` · ${counts.maybe} maybe` : ''}`
                : 'No responses yet'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
